import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Calendar, ArrowRight, Search } from "lucide-react";
import Reveal from "../components/Reveal";
import { resolveMediaUrl, responsiveSrcSet } from "../lib/media";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const readingTime = (html) => {
  const text = (html || "").replace(/<[^>]+>/g, " ").trim();
  const words = text.split(/\s+/).filter(Boolean).length;
  return Math.max(1, Math.round(words / 200));
};

export default function Blog() {
  const [blogs, setBlogs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [category, setCategory] = useState("ALL");
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState("latest"); // latest | oldest | featured

  useEffect(() => {
    let mounted = true;
    Promise.all([
      axios.get(`${API}/blogs?limit=200`),
      axios.get(`${API}/blog/categories`),
    ])
      .then(([b, c]) => {
        if (!mounted) return;
        setBlogs(Array.isArray(b.data) ? b.data : []);
        setCategories(Array.isArray(c.data) ? c.data : []);
      })
      .catch((err) =>
        console.warn("[Blog] /api/blogs fetch failed:", err)
      );
    return () => {
      mounted = false;
    };
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let list = blogs.filter((b) =>
      category === "ALL" ? true : b.category === category
    );
    if (q) {
      list = list.filter(
        (b) =>
          b.title.toLowerCase().includes(q) ||
          (b.excerpt || "").toLowerCase().includes(q) ||
          (b.category || "").toLowerCase().includes(q)
      );
    }
    if (sort === "oldest") {
      list = [...list].sort(
        (a, b) => new Date(a.published_at) - new Date(b.published_at)
      );
    } else if (sort === "featured") {
      list = [...list].sort((a, b) => (b.featured ? 1 : 0) - (a.featured ? 1 : 0));
    } else {
      list = [...list].sort(
        (a, b) => new Date(b.published_at) - new Date(a.published_at)
      );
    }
    return list;
  }, [blogs, category, query, sort]);

  const featured = useMemo(
    () => blogs.find((b) => b.featured) || blogs[0] || null,
    [blogs]
  );
  const rest = useMemo(
    () => filtered.filter((b) => !featured || b.id !== featured.id),
    [filtered, featured]
  );

  return (
    <div data-testid="blog-page" className="grain bg-[#f7f7f5]">
      {/* HERO */}
      <section className="border-b border-black/5 bg-white py-24 md:py-32">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <Reveal>
            <div className="font-mono-industrial text-[10px] uppercase tracking-[0.3em] text-[#e85a1c]">
              / Industry Insights
            </div>
            <h1 className="mt-5 font-display text-5xl uppercase leading-[0.9] tracking-tighter text-[#1c1c1e] md:text-7xl lg:text-8xl">
              The Baheti<br />
              <span className="text-[#e85a1c]">Steel Journal.</span>
            </h1>
            <p className="mt-7 max-w-2xl text-base leading-relaxed text-[#4a4a4e] md:text-lg">
              Steel market updates, TMT knowledge, construction insights, industry news, and the latest from our yards.
            </p>
          </Reveal>
        </div>
      </section>

      {/* FEATURED */}
      {featured && (
        <section className="border-b border-black/5 bg-[#f7f7f5] py-16 md:py-20">
          <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
            <Reveal>
              <div className="font-mono-industrial text-[10px] uppercase tracking-[0.3em] text-[#e85a1c]">
                / Featured article
              </div>
              <Link
                to={`/blog/${featured.slug}`}
                data-testid="blog-featured-card"
                className="group mt-6 grid overflow-hidden rounded-3xl border border-black/5 bg-white shadow-lux transition-all duration-500 hover:-translate-y-1 hover:shadow-lux-hover md:grid-cols-12"
              >
                <div className="relative h-64 overflow-hidden bg-[#efefe9] md:col-span-7 md:h-[460px]">
                  {featured.cover_image_url ? (
                    <img
                      src={resolveMediaUrl(featured.cover_image_url)}
                      srcSet={responsiveSrcSet(featured.cover_image_url, [480, 800, 1200, 1600])}
                      sizes="(min-width: 768px) 58vw, 100vw"
                      alt={featured.title}
                      loading="lazy"
                      decoding="async"
                      className="h-full w-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-110"
                    />
                  ) : (
                    <div className="flex h-full items-center justify-center font-display text-6xl text-[#e85a1c]/30">
                      ·
                    </div>
                  )}
                  <div className="absolute left-5 top-5 inline-flex items-center gap-2 rounded-full bg-[#e85a1c] px-3 py-1.5 font-mono-industrial text-[9px] uppercase tracking-[0.25em] text-white">
                    Featured · {featured.category}
                  </div>
                </div>
                <div className="flex flex-col justify-center p-7 md:col-span-5 md:p-12">
                  <div className="flex items-center gap-3 font-mono-industrial text-[10px] uppercase tracking-[0.25em] text-[#8b8b8f]">
                    <Calendar size={11} />
                    <span>{formatDate(featured.published_at)}</span>
                    <span>·</span>
                    <span>{readingTime(featured.content)} min read</span>
                  </div>
                  <h2 className="mt-4 font-display text-3xl uppercase leading-[0.98] tracking-tighter text-[#1c1c1e] md:text-5xl">
                    {featured.title}
                  </h2>
                  <p className="mt-5 line-clamp-4 text-[15px] leading-[1.7] text-[#4a4a4e]">
                    {featured.excerpt}
                  </p>
                  <span className="mt-7 inline-flex items-center gap-2 font-mono-industrial text-[11px] uppercase tracking-[0.25em] text-[#e85a1c]">
                    Read full article
                    <ArrowRight
                      size={13}
                      className="transition-transform duration-300 group-hover:translate-x-1"
                    />
                  </span>
                </div>
              </Link>
            </Reveal>
          </div>
        </section>
      )}

      {/* FILTERS + GRID */}
      <section className="border-b border-black/5 bg-white py-16 md:py-20">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <Reveal className="mb-10 flex flex-col gap-4 md:flex-row md:flex-wrap md:items-center md:justify-between">
            <div>
              <div className="font-mono-industrial text-[10px] uppercase tracking-[0.3em] text-[#e85a1c]">
                / All articles · {filtered.length}
              </div>
              <h2 className="mt-3 font-display text-3xl uppercase tracking-tighter text-[#1c1c1e] md:text-5xl">
                The journal.
              </h2>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <div className="relative flex-1 md:flex-none">
                <Search
                  size={14}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8b8b8f]"
                />
                <input
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search articles"
                  data-testid="blog-search"
                  className="w-full rounded-full border border-black/10 bg-white py-2 pl-9 pr-4 text-sm focus:border-[#e85a1c] focus:outline-none md:w-56"
                />
              </div>
              <select
                value={sort}
                onChange={(e) => setSort(e.target.value)}
                data-testid="blog-sort"
                className="rounded-full border border-black/10 bg-white px-3 py-2 text-xs font-semibold uppercase tracking-[0.15em] text-[#4a4a4e]"
              >
                <option value="latest">Latest</option>
                <option value="oldest">Oldest</option>
                <option value="featured">Featured</option>
              </select>
            </div>
          </Reveal>

          {/* Category chips */}
          <div className="mb-10 flex flex-wrap gap-2">
            {["ALL", ...categories].map((c) => (
              <button
                key={c}
                onClick={() => setCategory(c)}
                data-testid={`blog-cat-${c.replace(/\s+/g, "-").toLowerCase()}`}
                className={`rounded-full border px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] transition-all ${
                  category === c
                    ? "border-[#e85a1c] bg-[#e85a1c] text-white"
                    : "border-black/10 bg-white text-[#4a4a4e] hover:border-[#e85a1c] hover:text-[#e85a1c]"
                }`}
              >
                {c === "ALL" ? "All" : c}
              </button>
            ))}
          </div>

          {/* Grid */}
          {rest.length === 0 ? (
            <Reveal className="rounded-2xl border border-dashed border-black/10 bg-[#f7f7f5] p-14 text-center">
              <div className="font-display text-3xl uppercase tracking-tighter text-[#1c1c1e]">
                No articles match.
              </div>
              <p className="mt-3 text-sm text-[#8b8b8f]">
                Try a different category or search term.
              </p>
            </Reveal>
          ) : (
            <div className="grid grid-cols-1 gap-7 sm:grid-cols-2 lg:grid-cols-3">
              {rest.map((b, i) => (
                <Reveal key={b.id} delay={i * 70}>
                  <Link
                    to={`/blog/${b.slug}`}
                    data-testid={`blog-card-${b.slug}`}
                    className="group flex h-full flex-col overflow-hidden rounded-2xl border border-black/5 bg-white shadow-lux transition-all duration-300 hover:-translate-y-1 hover:shadow-lux-hover"
                  >
                    <div className="relative aspect-[16/10] overflow-hidden bg-[#efefe9]">
                      {b.cover_image_url ? (
                        <img
                          src={resolveMediaUrl(b.cover_image_url)}
                          srcSet={responsiveSrcSet(b.cover_image_url, [320, 480, 640, 800])}
                          sizes="(min-width: 1024px) 32vw, (min-width: 640px) 48vw, 92vw"
                          alt={b.title}
                          loading="lazy"
                          decoding="async"
                          className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-110"
                        />
                      ) : (
                        <div className="flex h-full items-center justify-center font-display text-6xl text-[#e85a1c]/30">
                          ·
                        </div>
                      )}
                      {b.featured && (
                        <div className="absolute left-3 top-3 rounded-full bg-white/90 px-2.5 py-1 font-mono-industrial text-[9px] uppercase tracking-[0.25em] text-[#e85a1c] backdrop-blur">
                          Featured
                        </div>
                      )}
                    </div>
                    <div className="flex flex-1 flex-col p-6">
                      <div className="font-mono-industrial text-[9px] uppercase tracking-[0.3em] text-[#e85a1c]">
                        {b.category}
                      </div>
                      <h3 className="mt-3 line-clamp-2 font-display-med text-xl uppercase leading-tight tracking-tight text-[#1c1c1e]">
                        {b.title}
                      </h3>
                      <p className="mt-3 line-clamp-3 flex-1 text-[13px] leading-[1.65] text-[#4a4a4e]">
                        {b.excerpt}
                      </p>
                      <div className="mt-5 flex items-center justify-between border-t border-black/5 pt-4">
                        <span className="font-mono-industrial text-[10px] uppercase tracking-[0.25em] text-[#8b8b8f]">
                          {formatDate(b.published_at)}
                        </span>
                        <span className="inline-flex items-center gap-1.5 font-mono-industrial text-[10px] uppercase tracking-[0.25em] text-[#e85a1c]">
                          Read
                          <ArrowRight
                            size={11}
                            className="transition-transform duration-300 group-hover:translate-x-1"
                          />
                        </span>
                      </div>
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
